import React, { useState, useContext, useEffect, useCallback } from "react";
import clsx from "clsx";
import proj4 from 'proj4';

// Custom Icons
import RullerOnIcon from "@/icons/RullerOnIcon";
import RullerOffIcon from "@/icons/RullerOffIcon";

// Custom Components
import ButtonControl from "@/components/Map/Controls/Custom/Button/ButtonControl";
import RadiusRulerRoot from "@/components/Map/Controls/Custom/RadiusRuler/RadiusRulerRoot";
import MapContext from "@/components/Map/MapContext";

// UI Components
import CenterCrosshair from "./ui/CentreCrosshair";
import RadiusCircle from "./ui/RadiusCircle"
import Compass from "./ui/Compass";

// Types
import { IRadiusRulerControl } from "@/@types/components/Map/Controls/Custom";
import { MapContextType } from "@/@types/context/MapContext";

type DistanceMap = {
    [key: number]: number;
  };

const DISTANCES: DistanceMap = {
    8: 50000,
    9: 25000,
    10: 15000,
    11: 10000,
    12: 5000,
    13: 2000,
    14: 1000,
    15: 500,
    16: 250,
    17: 100,
    18: 50,
    19: 25,
    20: 20
}

const DISTANCES_MOBILE: DistanceMap = {
    8: 40000,
    9: 20000,
    10: 10000,
    11: 5000,
    12: 2500,
    13: 1000,
    14: 500,
    15: 300,
    16: 150,
    17: 75,
    18: 40,
    19: 20,
    20: 10
}

function RadiusRulerControl(props: IRadiusRulerControl) {
    const { tooltip, target, className, circleCount = 7, iconColor } = props;
    const mapContext = useContext(MapContext) as MapContextType;
    const [active, setActive] = useState(false)
    const [pixelRadius, setPixelRadius] = useState(0)
    const [rotation, setRotation] = useState(0);

    const olMap = mapContext.map;
    const isNarrowScreen = window.innerWidth < 768;
    const activeDistances = isNarrowScreen ? DISTANCES_MOBILE : DISTANCES;

    // --------------------------- FUNCTIONS ------------------------------------------------------

    function getLabel(zoomLevel: number, multiplier: number) {
        const baseDistance = activeDistances[Math.round(zoomLevel)] ?? 0;
        const realDistance = baseDistance * multiplier
        const label = realDistance >= 1000
        ? `${realDistance / 1000} km` 
        : `${realDistance} m`;
        return label;
    }

    const updatePixelRadius = useCallback(() => {
        const zoom = olMap?.getView().getZoom();
        const resolution = olMap?.getView().getResolution();
        const center = olMap?.getView().getCenter();
        if (!center || !zoom) return;
    
        // Transform the center from Web Mercator to WGS 84 using proj4
        const wgs84Center = proj4('EPSG:3857', 'EPSG:4326', center);
        const latitude = wgs84Center[1] * (Math.PI / 180);  // Convert latitude to radians
    
        // Calculate scale factor for the current latitude
        const k = 1 / Math.cos(latitude);
    
        // Adjust the distance
        const roundedZoom = Math.round(zoom)
        const adjustedDistance = activeDistances[roundedZoom] * k;
    
        if (resolution) {
            const pixelRadius = adjustedDistance / resolution;
            setPixelRadius(pixelRadius);
        }
    }, [olMap]);

    const updateRotation = useCallback(() => {
        const rotation = olMap?.getView().getRotation();
        setRotation(rotation || 0);

    }, [olMap])

    // --------------------------- EFFECTS ------------------------------------------------------

    useEffect(() => {
        if (!olMap) return;
    
        if (active) {
            updatePixelRadius();
            updateRotation();
            olMap.getView().on('change:resolution', updatePixelRadius);
            olMap.getView().on('change:rotation', updateRotation);
        } else {
            olMap.getView().un('change:resolution', updatePixelRadius);
            olMap.getView().un('change:rotation', updateRotation);
        }
    
        return () => {
            const view = olMap.getView();
            view.un('change:resolution', updatePixelRadius);
            view.un('change:rotation', updateRotation);
        };
    }, [olMap, active, updatePixelRadius, updateRotation]);
    
    // --------------------------- RETURN ------------------------------------------------------

    const radiusCircleComponents = Array.from({ length: circleCount }, (_, index) => {
        const multiplier = index + 1;
        return (
            <RadiusCircle
                key={index}
                pixelRadius={pixelRadius * multiplier}
                label={getLabel(olMap?.getView().getZoom() ?? 0, multiplier)}
            />
        );
    });

    return (
        <>
            <ButtonControl
                id="radius-ruler-btn"
                className={clsx(className, "ol-radius-ruler")}
                title={tooltip}
                onClick={() => setActive(!active)}
                target={target}
                sx={{
                    bgcolor: active ? "primary.dark" : "primary.light",
                }}
                disableFocusRipple
                disableRipple
                disableTouchRipple
                disableElevation
            >
                {active ? <RullerOnIcon fontSize="small" iconColor={iconColor} /> : <RullerOffIcon fontSize="small" iconColor={iconColor} />}
            </ButtonControl>
            <RadiusRulerRoot>
                {active ? 
                <>
                    <CenterCrosshair />
                    <Compass pixelRadius={pixelRadius * 2} rotation={rotation}/>
                    {
                        ...radiusCircleComponents.filter((x,i) => i !== 1) //skip second circle
                    }
                </>
                : null}
            </RadiusRulerRoot>
        </>
    );
}

export default RadiusRulerControl;